import { execa } from 'execa';
import { pathExists } from '@wix/cli-fs';
import { dirname } from 'node:path';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { bin, install } from 'cloudflared';
import type { TunnelClient } from './client.js';

export interface Connection {
  close: () => void;
  domain: string;
}

export async function launchTunnel(
  port: number,
  tunnelClient: TunnelClient
): Promise<Connection> {
  const { localTunnel } = getTestOverrides();

  if (!localTunnel && !(await pathExists(bin))) {
    try {
      await install(bin);
    } catch (e) {
      const err = e as NodeJS.ErrnoException;
      if (e instanceof Error && err.code === 'EACCES') {
        throw new CliError({
          code: CliErrorCode.FailedInstallCloudflareNoAccess({
            path: dirname(err.path!),
          }),
          cause: e,
        });
      }
      throw e;
    }
  }

  const tunnel = await tunnelClient.createTunnel({ port });
  const domain = `http${localTunnel ? '' : 's'}://${tunnel.domain}`;

  const createTunnelPromise = new Promise<Connection>((resolve, reject) => {
    const tunnelProcess = localTunnel
      ? execa('echo', [tunnel.domain, tunnel.token], { detached: true })
      : execa(bin, ['tunnel', 'run', '--token', tunnel.token]);

    const close = () => {
      tunnelProcess.kill();
    };

    const listener = async (data: Buffer) => {
      const output = data.toString();
      const match = output.match(tunnel.domain)?.[0];

      if (match) {
        resolve({
          domain,
          close,
        });
      }
    };

    tunnelProcess.stderr?.on('data', listener);
    tunnelProcess.stdout?.on('data', listener);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    tunnelProcess.on('error', (err) => {
      reject(err);
    });
  });

  return {
    domain,
    close: async () => {
      const tunnel = await createTunnelPromise;
      return tunnel.close();
    },
  };
}
